import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Plus, RefreshCw, Download, Search, Eye, Edit, Trash2, Upload, UserX, Filter, Printer, FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import SectionCard from '@/components/dashboard/SectionCard';
import TrackerSummary from '@/components/dashboard/TrackerSummary';
import ABTImportModal from '@/components/modals/ABTImportModal';
import ABTCaseModal from '@/components/modals/ABTCaseModal';
import ABTReviewNoteModal from '@/components/modals/ABTReviewNoteModal';
import { loadDB, getActiveABT, saveDB, addAudit } from '@/lib/database';
import { ABTRecord, ViewType } from '@/lib/types';
import { isoDateFromAny, computeTxDays, mrnMatchKeys, todayISO } from '@/lib/parsers';
import { useToast } from '@/hooks/use-toast';
import { SortableTableHeader, useSortableTable } from '@/components/ui/sortable-table-header';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import TablePagination from '@/components/ui/table-pagination';
import { recordWorkflowMetric } from '@/lib/analytics/workflowMetrics';
import { exportABTToCSV, importABTFromCSV } from '@/lib/utils/abtCsvService';

const escapeHtml = (value: string | number | boolean | null | undefined): string => {
  if (value === null || value === undefined) return '';
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
};

interface ABTViewProps {
  onNavigate?: (view: ViewType) => void;
  initialStatusFilter?: 'active' | 'completed' | 'all';
}

const ABTView = ({ onNavigate, initialStatusFilter }: ABTViewProps) => {
  const { toast } = useToast();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'completed'>('active');
  const [unitFilter, setUnitFilter] = useState('all');
  const [startDateFilter, setStartDateFilter] = useState('');
  const [endDateFilter, setEndDateFilter] = useState('');
  const [showImportModal, setShowImportModal] = useState(false);
  const [showCaseModal, setShowCaseModal] = useState(false);
  const [showReviewNoteModal, setShowReviewNoteModal] = useState(false);
  const [editingRecord, setEditingRecord] = useState<ABTRecord | null>(null);
  const [reviewingRecord, setReviewingRecord] = useState<ABTRecord | null>(null);
  const [db, setDb] = useState(() => loadDB());
  const [currentPage, setCurrentPage] = useState(1);
  const csvInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (initialStatusFilter) {
      setStatusFilter(initialStatusFilter);
    }
  }, [initialStatusFilter]);
  
  const records = db.records.abx;
  const today = todayISO();
  const units = useMemo(
    () => [...new Set(records.map(r => r.unit).filter(Boolean))].sort((a, b) => a.localeCompare(b)),
    [records],
  );

  const deriveStatus = (record: ABTRecord): 'active' | 'completed' | 'discontinued' => {
    const status = (record.status || '').toLowerCase();
    if (status === 'discontinued') return 'discontinued';
    const endDate = record.endDate || record.end_date;
    if (!endDate) return 'active';
    const endIso = isoDateFromAny(endDate);
    if (!endIso) return 'completed';
    return endIso >= today ? 'active' : 'completed';
  };

  const handleDeleteRecord = (record: ABTRecord) => {
    if (!window.confirm(`Delete ABT record for ${record.residentName || record.name}?`)) return;
    
    const currentDb = loadDB();
    const idx = currentDb.records.abx.findIndex(r => r.id === record.id);
    if (idx >= 0) {
      currentDb.records.abx.splice(idx, 1);
      addAudit(currentDb, 'abx_delete', `Deleted ABT record for ${record.residentName || record.name}`, 'abt');
      saveDB(currentDb);
      setDb(currentDb);
      toast({ title: 'ABT Record Deleted' });
    }
  };

  const handleDischargeRecord = (record: ABTRecord) => {
    if (!window.confirm(`Mark ${record.residentName || record.name} as discharged? This will close the ABT record.`)) return;
    
    const currentDb = loadDB();
    const idx = currentDb.records.abx.findIndex(r => r.id === record.id);
    if (idx >= 0) {
      currentDb.records.abx[idx].status = 'discontinued';
      currentDb.records.abx[idx].endDate = today;
      currentDb.records.abx[idx].end_date = today;
      currentDb.records.abx[idx].notes = `${currentDb.records.abx[idx].notes || ''} [Manual discharge: ${today}]`.trim();
      addAudit(currentDb, 'abx_discharge', `Manually discharged ABT record for ${record.residentName || record.name}`, 'abt');
      saveDB(currentDb);
      setDb(currentDb);
      toast({ title: 'ABT Record Discharged', description: `${record.residentName || record.name} marked as discharged` });
    }
  };

  const handleOpenReviewNote = (record: ABTRecord) => {
    setReviewingRecord(record);
    setShowReviewNoteModal(true);
  };

  const handleReviewNoteClose = () => {
    setShowReviewNoteModal(false);
    setReviewingRecord(null);
  };

  const handleReviewNoteSave = () => {
    setDb(loadDB());
    setShowReviewNoteModal(false);
    setReviewingRecord(null);
  };

  // Get active resident MRNs from census
  const activeCensusMrns = new Set<string>();
  Object.values(db.census.residentsByMrn)
    .filter(r => r.active_on_census)
    .forEach(r => {
      mrnMatchKeys(r.mrn).forEach(key => activeCensusMrns.add(key));
    });

  const isActiveCensusMrn = (mrn?: string) => {
    const matchKeys = mrnMatchKeys(mrn || '');
    if (matchKeys.length === 0) return true;
    return matchKeys.some(key => activeCensusMrns.has(key));
  };

  const filteredRecords = records.filter(r => {
    // Always exclude discontinued records from active view
    if (deriveStatus(r) === 'discontinued') {
      // Only show discontinued in 'completed' filter or 'all' filter
      if (statusFilter === 'active') return false;
    }
    
    // Check if resident is still in census (unless viewing completed/all)
    if (statusFilter === 'active') {
      if (r.mrn && !isActiveCensusMrn(r.mrn)) {
        return false; // Resident discharged from census
      }
    }
    
    // Search filter
    const name = r.residentName || r.name || '';
    const medication = r.medication || r.med_name || '';
    const indication = r.indication || '';
    
    const normalizedSearch = searchTerm.trim().toLowerCase();
    const matchesSearch = normalizedSearch.length === 0
      || name.toLowerCase().includes(normalizedSearch)
      || medication.toLowerCase().includes(normalizedSearch)
      || indication.toLowerCase().includes(normalizedSearch)
      || (r.unit || '').toLowerCase().includes(normalizedSearch)
      || (r.room || '').toLowerCase().includes(normalizedSearch);
    
    if (!matchesSearch) return false;

    if (unitFilter !== 'all' && r.unit !== unitFilter) return false;

    const startIso = isoDateFromAny(r.startDate || r.start_date || '');

    // Date filters are based on ABT initiation/start date only.
    // If a date range is selected, require a valid start date and keep records
    // where start date falls within the selected bounds.
    if ((startDateFilter || endDateFilter) && !startIso) return false;
    if (startDateFilter && startIso < startDateFilter) return false;
    if (endDateFilter && startIso > endDateFilter) return false;
    
    // Status filter
    if (statusFilter === 'all') return true;
    if (statusFilter === 'active') {
      return deriveStatus(r) === 'active';
    }
    if (statusFilter === 'completed') {
      return deriveStatus(r) !== 'active';
    }
    return true;
  });

  const getNormalizedTxDays = useCallback((record: ABTRecord): number | null => {
    // Preserve previously stored DOT values for existing records.
    // Only compute when historical imports/entries are missing tx_days fields.
    const storedTxDays = record.tx_days ?? record.daysOfTherapy;
    if (storedTxDays !== null && storedTxDays !== undefined) {
      return storedTxDays;
    }

    const startDate = record.startDate || record.start_date || '';
    const endDate = record.endDate || record.end_date || today;
    return computeTxDays(startDate, endDate);
  }, [today]);

  // Prepare data for sorting
  const recordsWithSortableFields = useMemo(() => filteredRecords.map(r => ({
    ...r,
    _name: r.residentName || r.name || '',
    _medication: r.medication || r.med_name || '',
    _startDate: isoDateFromAny(r.startDate || r.start_date || ''),
    _endDate: isoDateFromAny(r.endDate || r.end_date || ''),
    _txDays: getNormalizedTxDays(r) || 0
  })), [filteredRecords, getNormalizedTxDays]);

  const { sortKey, sortDirection, handleSort, sortedData: sortedRecords } = useSortableTable(recordsWithSortableFields, '_startDate', 'desc');
  const pageSize = 20;
  const totalPages = Math.max(1, Math.ceil(sortedRecords.length / pageSize));
  const startIndex = (currentPage - 1) * pageSize;
  const pagedRecords = sortedRecords.slice(startIndex, startIndex + pageSize);
  const rangeStart = sortedRecords.length === 0 ? 0 : startIndex + 1;
  const rangeEnd = Math.min(startIndex + pageSize, sortedRecords.length);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter, unitFilter, startDateFilter, endDateFilter]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const activeCount = records.filter(r => {
    if (deriveStatus(r) === 'discontinued') return false;
    if (r.mrn && !isActiveCensusMrn(r.mrn)) return false;
    return deriveStatus(r) === 'active';
  }).length;

  const completedCount = records.filter(r => deriveStatus(r) !== 'active').length;

  const getStatusBadge = (record: typeof records[0]) => {
    const derivedStatus = deriveStatus(record);

    if (derivedStatus === 'discontinued') {
      return <span className="badge-status badge-bad">Discontinued</span>;
    }
    if (derivedStatus === 'active') {
      return <span className="badge-status badge-warn">Active</span>;
    }
    return <span className="badge-status badge-ok">Completed</span>;
  };

  const formatDate = (dateStr: string | undefined) => {
    if (!dateStr) return '—';
    const iso = isoDateFromAny(dateStr);
    if (!iso) return dateStr;
    const d = new Date(iso + 'T00:00:00');
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' });
  };

  const handleRefresh = () => {
    setDb(loadDB());
  };

  const handleExport = () => {
    recordWorkflowMetric({
      eventName: 'workflow_report_quick_run',
      view: 'abt',
      metadata: { caseType: 'abt', reportType: 'abt_export_csv', resultCount: records.length },
    });
    exportABTToCSV();
  };

  const handleCsvImport = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] || null;
    event.target.value = '';
    if (!file) {
      return;
    }

    try {
      const result = await importABTFromCSV(file);
      if (result.newCount > 0 || result.updateCount > 0) {
        setDb(loadDB());
        toast({ title: 'ABT CSV import complete', description: `${result.newCount} new, ${result.updateCount} updated.` });
      }
    } catch (error) {
      toast({
        title: 'ABT CSV import failed',
        description: error instanceof Error ? error.message : 'Unable to parse CSV file.',
        variant: 'destructive',
      });
    }
  };

  const handlePrintFilteredView = () => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      toast({ title: 'Unable to print', description: 'Please allow popups to print this report.', variant: 'destructive' });
      return;
    }

    const appliedFilters = [
      `Status: ${statusFilter}`,
      `Unit: ${unitFilter === 'all' ? 'All units' : unitFilter}`,
      `Start from: ${startDateFilter || 'Any'}`,
      `End by: ${endDateFilter || 'Any'}`,
      `Search: ${searchTerm.trim() || 'None'}`,
    ].join(' • ');

    const tableRows = sortedRecords.map(record => {
      const residentName = record.residentName || record.name || '—';
      const medication = record.medication || record.med_name || '—';
      const courseStatus = deriveStatus(record);

      return `
        <tr>
          <td>${escapeHtml(residentName)}</td>
          <td>${escapeHtml(`${record.unit || '—'} / ${record.room || '—'}`)}</td>
          <td>${escapeHtml(medication)}</td>
          <td>${escapeHtml(record.dose || '—')}</td>
          <td>${escapeHtml(record.route || '—')}</td>
          <td>${escapeHtml(record.indication || '—')}</td>
          <td>${escapeHtml(record.infection_source || '—')}</td>
          <td>${escapeHtml(formatDate(record.startDate || record.start_date))}</td>
          <td>${escapeHtml(formatDate(record.endDate || record.end_date))}</td>
          <td>${escapeHtml(getNormalizedTxDays(record) ?? '—')}</td>
          <td>${escapeHtml(courseStatus)}</td>
        </tr>
      `;
    }).join('');

    const emptyState = `
      <tr>
        <td colspan="11" style="text-align:center; padding: 18px; color:#64748b;">No ABT records match the selected filters.</td>
      </tr>
    `;

    printWindow.document.write(`
      <!doctype html>
      <html>
        <head>
          <title>ABT Filtered View</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; color: #0f172a; }
            h1 { margin: 0 0 8px; font-size: 20px; }
            .meta { margin-bottom: 12px; font-size: 12px; color: #475569; line-height: 1.5; }
            table { border-collapse: collapse; width: 100%; font-size: 11px; }
            th, td { border: 1px solid #cbd5e1; padding: 6px; text-align: left; vertical-align: top; }
            th { background: #f1f5f9; font-weight: 700; }
            @media print {
              @page { margin: 12mm; }
              body { margin: 0; }
            }
          </style>
        </head>
        <body>
          <h1>ABT Management — Filtered View</h1>
          <div class="meta">
            Generated: ${escapeHtml(new Date().toLocaleString())}<br />
            Filters: ${escapeHtml(appliedFilters)}<br />
            Total records: ${escapeHtml(sortedRecords.length)}
          </div>
          <table>
            <thead>
              <tr>
                <th>Resident</th>
                <th>Unit/Room</th>
                <th>Medication</th>
                <th>Dose</th>
                <th>Route</th>
                <th>Indication</th>
                <th>Source</th>
                <th>Start</th>
                <th>End</th>
                <th>Days</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              ${tableRows || emptyState}
            </tbody>
          </table>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-foreground">ABT Management</h2>
          <p className="text-sm text-muted-foreground">Antibiotic Therapy Tracking & Stewardship</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={handleRefresh}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Button variant="outline" size="sm" onClick={handleExport}>
            <Download className="w-4 h-4 mr-2" />
            Export CSV
          </Button>
          <input
            ref={csvInputRef}
            type="file"
            accept=".csv,text/csv"
            className="hidden"
            onChange={handleCsvImport}
          />
          <Button variant="outline" size="sm" onClick={() => csvInputRef.current?.click()}>
            <Upload className="w-4 h-4 mr-2" />
            Import CSV
          </Button>
          <Button variant="outline" size="sm" onClick={handlePrintFilteredView}>
            <Printer className="w-4 h-4 mr-2" />
            Print filtered
          </Button>
          <Button variant="outline" size="sm" onClick={() => setShowImportModal(true)}>
            <Upload className="w-4 h-4 mr-2" />
            Import Wizard
          </Button>
          <Button size="sm" onClick={() => { setEditingRecord(null); setShowCaseModal(true); }}>
            <Plus className="w-4 h-4 mr-2" />
            Add ABT
          </Button>
        </div>
      </div>

      {/* Trend Summary */}
      <TrackerSummary type="abt" />

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="stat-card text-center">
          <div className="text-2xl font-bold text-primary">{activeCount}</div>
          <div className="text-sm text-muted-foreground">Active Courses</div>
        </div>
        <div className="stat-card text-center">
          <div className="text-2xl font-bold text-foreground">{records.length}</div>
          <div className="text-sm text-muted-foreground">Total Records</div>
        </div>
        <div className="stat-card text-center">
          <div className="text-2xl font-bold text-success">{completedCount}</div>
          <div className="text-sm text-muted-foreground">Completed</div>
        </div>
        <div className="stat-card text-center">
          <div className="text-2xl font-bold text-warning">
            {records.length > 0 
              ? Math.round(records.reduce((sum, r) => sum + (r.tx_days || r.daysOfTherapy || 0), 0) / records.length)
              : 0}
          </div>
          <div className="text-sm text-muted-foreground">Avg. Days Therapy</div>
        </div>
      </div>

      {/* Filter */}
      <div className="filter-panel">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input 
                placeholder="Search by name, medication, or indication..." 
                className="pl-10"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Filter className="h-4 w-4 text-muted-foreground" />
            <Select value={unitFilter} onValueChange={setUnitFilter}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Unit" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All units</SelectItem>
                {units.map(unit => (
                  <SelectItem key={unit} value={unit}>{unit}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input
              type="date"
              value={startDateFilter}
              onChange={(e) => setStartDateFilter(e.target.value)}
              className="w-[150px]"
              aria-label="Start date filter"
            />
            <Input
              type="date"
              value={endDateFilter}
              onChange={(e) => setEndDateFilter(e.target.value)}
              className="w-[150px]"
              aria-label="End date filter"
            />
            <Badge 
              variant="outline" 
              className={`cursor-pointer ${statusFilter === 'all' ? 'bg-primary text-primary-foreground' : 'hover:bg-primary hover:text-primary-foreground'}`}
              onClick={() => setStatusFilter('all')}
            >
              All ({records.length})
            </Badge>
            <Badge 
              variant="outline" 
              className={`cursor-pointer ${statusFilter === 'active' ? 'bg-primary text-primary-foreground' : 'hover:bg-primary hover:text-primary-foreground'}`}
              onClick={() => setStatusFilter('active')}
            >
              Active ({activeCount})
            </Badge>
            <Badge 
              variant="outline" 
              className={`cursor-pointer ${statusFilter === 'completed' ? 'bg-primary text-primary-foreground' : 'hover:bg-primary hover:text-primary-foreground'}`}
              onClick={() => setStatusFilter('completed')}
            >
              Completed ({completedCount})
            </Badge>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setSearchTerm('');
                setStatusFilter('active');
                setUnitFilter('all');
                setStartDateFilter('');
                setEndDateFilter('');
              }}
            >
              Clear filters
            </Button>
          </div>
        </div>
      </div>

      {/* Table */}
      <SectionCard title={`ABT Records (${sortedRecords.length})`} noPadding>
        <div className="overflow-x-auto">
          <table className="data-table">
            <thead>
              <tr>
                <SortableTableHeader label="Resident" sortKey="_name" currentSortKey={sortKey} currentSortDirection={sortDirection} onSort={handleSort} />
                <SortableTableHeader label="Unit/Room" sortKey="unit" currentSortKey={sortKey} currentSortDirection={sortDirection} onSort={handleSort} />
                <SortableTableHeader label="Medication" sortKey="_medication" currentSortKey={sortKey} currentSortDirection={sortDirection} onSort={handleSort} />
                <SortableTableHeader label="Dose" sortKey="dose" currentSortKey={sortKey} currentSortDirection={sortDirection} onSort={handleSort} />
                <SortableTableHeader label="Route" sortKey="route" currentSortKey={sortKey} currentSortDirection={sortDirection} onSort={handleSort} />
                <SortableTableHeader label="Indication" sortKey="indication" currentSortKey={sortKey} currentSortDirection={sortDirection} onSort={handleSort} />
                <th>Source</th>
                <SortableTableHeader label="Start" sortKey="_startDate" currentSortKey={sortKey} currentSortDirection={sortDirection} onSort={handleSort} />
                <SortableTableHeader label="End" sortKey="_endDate" currentSortKey={sortKey} currentSortDirection={sortDirection} onSort={handleSort} />
                <SortableTableHeader label="Days" sortKey="_txDays" currentSortKey={sortKey} currentSortDirection={sortDirection} onSort={handleSort} />
                <SortableTableHeader label="Status" sortKey="status" currentSortKey={sortKey} currentSortDirection={sortDirection} onSort={handleSort} />
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortedRecords.length === 0 ? (
                <tr>
                  <td colSpan={12} className="text-center py-8 text-muted-foreground">
                    No ABT records found. Import data or add a new record.
                  </td>
                </tr>
              ) : (
                pagedRecords.map((record, idx) => (
                  <tr key={record.id || idx}>
                    <td className="font-medium">{record.residentName || record.name}</td>
                    <td className="text-sm">{record.unit} / {record.room}</td>
                    <td>
                      <span className="font-semibold text-primary text-sm">{record.medication || record.med_name}</span>
                    </td>
                    <td className="text-sm">{record.dose}</td>
                    <td className="text-sm">
                      <span className="badge-status badge-abt">{record.route || '—'}</span>
                    </td>
                    <td className="text-sm max-w-[120px] truncate" title={record.indication}>
                      {record.indication}
                    </td>
                    <td>
                      <span className="badge-status badge-abt text-xs">{record.infection_source || '—'}</span>
                    </td>
                    <td className="text-sm">{formatDate(record.startDate || record.start_date)}</td>
                    <td className="text-sm">{formatDate(record.endDate || record.end_date)}</td>
                    <td className="font-semibold">{getNormalizedTxDays(record) ?? '—'}</td>
                    <td>{getStatusBadge(record)}</td>
                    <td>
                      <div className="flex items-center gap-1">
                        <button 
                          type="button"
                          className="row-action-btn" 
                          title="View"
                          onClick={() => { setEditingRecord(record); setShowCaseModal(true); }}
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button 
                          type="button"
                          className="row-action-btn" 
                          title="Edit"
                          onClick={() => { setEditingRecord(record); setShowCaseModal(true); }}
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleOpenReviewNote(record)}
                          title="Generate Review Progress Note"
                          className="h-8 border-blue-600 text-blue-600 hover:bg-blue-50"
                        >
                          <FileText className="w-3 h-3 mr-1" />
                          Note
                        </Button>
                        <button 
                          type="button"
                          className="row-action-btn" 
                          title="Delete"
                          onClick={() => handleDeleteRecord(record)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                        {deriveStatus(record) === 'active' && (
                          <button 
                            type="button"
                            className="row-action-btn text-warning hover:text-warning" 
                            title="Discharge"
                            onClick={() => handleDischargeRecord(record)}
                          >
                            <UserX className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <TablePagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
          totalItems={sortedRecords.length}
          rangeStart={rangeStart}
          rangeEnd={rangeEnd}
          itemLabel="records"
        />
      </SectionCard>

      <ABTImportModal 
        open={showImportModal}
        onClose={() => setShowImportModal(false)}
        onImportComplete={() => setDb(loadDB())}
      />
      
      <ABTCaseModal 
        open={showCaseModal}
        onClose={() => { setShowCaseModal(false); setEditingRecord(null); }}
        onSave={() => setDb(loadDB())}
        editRecord={editingRecord}
      />

      {reviewingRecord && (
        <ABTReviewNoteModal
          open={showReviewNoteModal}
          onClose={handleReviewNoteClose}
          onSave={handleReviewNoteSave}
          abtRecord={reviewingRecord}
        />
      )}

      {onNavigate && (
        <SectionCard title="Next Steps">
          <div className="flex flex-wrap gap-2">
            <Button size="sm" onClick={() => onNavigate('ip')}>
              Review IP Cases
            </Button>
            <Button size="sm" variant="outline" onClick={() => onNavigate('notes')}>
              Capture Notes
            </Button>
            <Button size="sm" variant="outline" onClick={() => onNavigate('reports')}>
              Generate Report
            </Button>
          </div>
        </SectionCard>
      )}
    </div>
  );
};

export default ABTView;
